import React, { Component } from 'react';
import { buildPath } from '../utils/route_utils.js';
import { updateSEO } from '../utils/seo_utils';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import AnnouncementBanner from '../components/AnnouncementBanner';
import Breadcrumb from '../components/Breadcrumb';
import '../css/CoreTechnologyPage.css';

class CoreTechnologyPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      lang: this.props.lang,
      activeTab: 0,
      visibleSections: {}
    };
    this.update_lang = this.update_lang.bind(this);
    this.sectionRefs = [];
    this.observerCallback = this.observerCallback.bind(this);
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    updateSEO('core_technology', this.state.lang, '/core-technology');
    this.observer = new IntersectionObserver(this.observerCallback, {
      threshold: 0.15,
      rootMargin: '0px 0px -50px 0px'
    });
    this.sectionRefs.forEach(ref => {
      if (ref) this.observer.observe(ref);
    });
  }

  componentWillUnmount() {
    if (this.observer) this.observer.disconnect();
  }

  observerCallback(entries) {
    const updates = {};
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        updates[entry.target.dataset.section] = true;
      }
    });
    if (Object.keys(updates).length > 0) {
      this.setState(prev => ({ visibleSections: { ...prev.visibleSections, ...updates } }));
    }
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.lang !== this.state.lang) {
      this.setState({ lang: nextProps.lang });
    }
  }

  update_lang(newLang) {
    this.props.updateLang(newLang);
  }

  navigate(route) {
    window.location.href = buildPath(this.state.lang, route);
  }

  registerRef(el, id) {
    if (el && !this.sectionRefs.includes(el)) {
      el.dataset.section = id;
      this.sectionRefs.push(el);
      if (this.observer) this.observer.observe(el);
    }
  }

  render() {
    const lang = this.state.lang;

    const hero = {
      tag: { zh: "AI ROBOTICS", en: "AI ROBOTICS", ja: "AI ROBOTICS" },
      title: {
        zh: "核心技術",
        en: "Core Technology",
        ja: "コア技術"
      },
      subtitle: {
        zh: "從感測、控制到決策與學習的完整技術鏈條",
        en: "Full-stack capabilities from sensing and control to decision-making and learning",
        ja: "センシングから制御、意思決定、学習までの完全な技術チェーン"
      }
    };

    const introText = {
      zh: "Nexuni 專注於打造具備高效感知、即時決策與彈性部署能力的 AI 機器人平台。我們的核心技術涵蓋從感測與控制到決策與學習的完整鏈條，並以模組化與邊緣運算為核心策略，讓機器人能靈活應對各類動態場域與任務需求。",
      en: "Nexuni focuses on building AI robotic platforms with advanced perception, real-time decision-making, and flexible deployment capabilities. Our core technologies span the full stack from sensing and control to decision-making and learning. By leveraging modular design and edge computing, our robots can adapt to a wide range of dynamic environments and task requirements.",
      ja: "Nexuni は、高効率なセンシング、リアルタイムな意思決定、柔軟な導入を可能にする AI ロボットプラットフォームの開発に注力しています。センシング、制御、意思決定、学習までを網羅した技術チェーンを自社で構築しており、モジュール化とエッジコンピューティングを中核戦略として、さまざまな動的環境やタスク要求に柔軟に対応できるロボットを実現します。"
    };

    const pillarIcons = [
      // Gear/cog icon for Core Tech
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>,
      // CPU/chip icon for Strategy
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="4" width="16" height="16" rx="2"/><rect x="9" y="9" width="6" height="6"/><line x1="9" y1="1" x2="9" y2="4"/><line x1="15" y1="1" x2="15" y2="4"/><line x1="9" y1="20" x2="9" y2="23"/><line x1="15" y1="20" x2="15" y2="23"/><line x1="20" y1="9" x2="23" y2="9"/><line x1="20" y1="14" x2="23" y2="14"/><line x1="1" y1="9" x2="4" y2="9"/><line x1="1" y1="14" x2="4" y2="14"/></svg>,
      // Globe/network icon for Market
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>
    ];

    const pillars = [
      {
        iconIdx: 0,
        label: { zh: "核心技術優勢", en: "Core Technical Advantages", ja: "コア技術の優位性" },
        color: "var(--main-color)",
        title: {
          zh: "從感知控制到學習決策的全棧 AI 機器人平台",
          en: "Full-Stack AI Robotics: From Perception to Learned Decision-Making",
          ja: "知覚から学習型意思決定までのフルスタック AI ロボットプラットフォーム"
        },
        bullets: {
          zh: [
            "自研機器人控制架構，整合感測、運動控制、定位（視覺 SLAM、RTK GPS、IMU 融合）與路徑規劃，適應室內外動態環境",
            "多感測器同步紀錄（RGBD、LiDAR、IMU）搭配自研標註與資料處理管線，持續建立高品質訓練資料集並加速模型迭代",
            "強化學習訓練四足與人形機器人運動控制，實現複雜地形中的穩定行走與平衡恢復",
            "端到端 VLA 模型實現機器人操作任務的自主規劃",
            "人類示範策略學習，從少量示範中快速習得操作技能，大幅縮短部署時間"
          ],
          en: [
            "Proprietary control architecture integrating sensing, motion control, localization (visual SLAM, RTK GPS, IMU fusion), and path planning for dynamic indoor and outdoor environments",
            "Multi-sensor synchronized logging (RGBD, LiDAR, IMU) with in-house annotation and data processing pipelines for continuous high-quality dataset building and model iteration",
            "Reinforcement learning for quadruped and humanoid locomotion — stable walking, running, and balance recovery on complex terrains",
            "End-to-end VLA models for autonomous robot manipulation planning",
            "Policy learning from human demonstration — robots acquire skills from minimal demos, reducing deployment time"
          ],
          ja: [
            "センサー・運動制御・測位（視覚 SLAM、RTK GPS、IMU 融合）・経路計画を統合する自社開発制御アーキテクチャで、屋内外の動的環境に対応",
            "RGBD・LiDAR・IMU の同期記録と自社開発アノテーション・データ処理パイプラインにより、高品質データセットの継続構築とモデル反復を加速",
            "強化学習による四足・ヒューマノイドの歩行制御 — 複雑地形での安定歩行・バランス回復を実現",
            "エンドツーエンド VLA モデルによるロボット操作の自律計画",
            "人間のデモからの方策学習 — 少数デモからスキルを迅速に獲得し、導入時間を大幅短縮"
          ]
        }
      },
      {
        iconIdx: 1,
        label: { zh: "技術實現策略", en: "Technology Implementation Strategy", ja: "技術実現戦略" },
        color: "var(--sub2-color)",
        title: {
          zh: "輕量化 AI 模型與邊緣計算整合",
          en: "Lightweight AI Models and Edge Computing Integration",
          ja: "軽量 AI モデルとエッジコンピューティングの統合"
        },
        bullets: {
          zh: [
            "採用 TinyML、模型剪枝（Pruning）與量化（Quantization）等技術，將 AI 模型壓縮後部署於機器人邊緣裝置上進行即時推論",
            "整合 NVIDIA 深度學習與推論加速框架（如 TensorRT、ISAAC），實現低延遲的控制訊號處理與即時場景感知",
            "AI 模型支援區域自適應學習能力，能根據不同環境條件自動調整參數，強化在非結構場域下的任務適應性與穩定性"
          ],
          en: [
            "Uses TinyML, model pruning, and quantization to compress AI models for real-time inference on edge devices",
            "Integrates NVIDIA deep learning and inference acceleration frameworks (TensorRT, ISAAC) for low-latency control signal processing and real-time perception",
            "Supports local adaptive learning, allowing models to adjust parameters based on environmental conditions for enhanced stability in unstructured environments"
          ],
          ja: [
            "TinyML、モデルプルーニング、量子化などの技術を用いてモデルを軽量化し、ロボットのエッジデバイスでリアルタイム推論を実行",
            "TensorRT や ISAAC など NVIDIA の推論加速フレームワークを統合し、低遅延の制御信号処理とリアルタイムな認識を実現",
            "環境条件に応じて自動でパラメータを調整する局所適応型学習機能を搭載し、非構造環境での柔軟かつ安定した動作を実現"
          ]
        }
      },
      {
        iconIdx: 2,
        label: { zh: "市場應用靈活性", en: "Market Application Flexibility", ja: "市場への柔軟な応用力" },
        color: "var(--sub1-color)",
        title: {
          zh: "彈性導入多場域智慧自動化解決方案",
          en: "Flexible Deployment Across Diverse Automation Scenarios",
          ja: "多様な現場に柔軟に対応するスマート自動化ソリューション"
        },
        bullets: {
          zh: [
            "具備端對端整合能力，從機器人底層韌體到上層應用控制皆可彈性調整，支援現場工程導入時的快速部署與調整",
            "模組化介面設計支援 API 開發與第三方系統串接，可無縫整合至客戶現有場域系統，擴展智慧工廠、智慧建築與自動化物流等應用場景"
          ],
          en: [
            "End-to-end integration capabilities from low-level firmware to high-level application control, enabling quick adaptation and deployment in the field",
            "Modular interface design supports API development and third-party system integration, enabling seamless adoption into smart factories, intelligent buildings, and automated logistics"
          ],
          ja: [
            "ロボットのファームウェアからアプリケーション制御まで、エンドツーエンドで柔軟に対応し、現場での迅速な導入と調整が可能",
            "モジュール型インターフェース設計により API 開発やサードパーティシステムとの連携に対応し、スマート工場、スマートビル、物流自動化などへの拡張を支援"
          ]
        }
      }
    ];

    const patents = {
      title: {
        zh: "研發成果、專利及認證",
        en: "Research Achievements, Patents & Certifications",
        ja: "研究成果、特許および認証"
      },
      desc: {
        zh: "我們的團隊擁有強大的研發實力，至今已在台灣、美國及新加坡獲得超過十五項專利，涵蓋機器人技術、自動化系統和人工智慧等領域。我們已獲得 Universal Robots 認證，成為其 Certified Solution Partner。",
        en: "Our team has obtained over fifteen patents in Taiwan, the United States, and Singapore, covering robotics, automation systems, and AI. We are a Certified Solution Partner of Universal Robots, a global leader in collaborative robots.",
        ja: "私たちのチームは台湾、アメリカ、シンガポールで15以上の特許を取得しています。ロボット技術、自動化システム、AI人工知能の分野をカバーし、Universal Robots の Certified Solution Partner として認定されています。"
      },
      stats: [
        { num: "15+", label: { zh: "項專利", en: "Patents", ja: "件の特許" } },
        { num: "3", label: { zh: "個國家", en: "Countries", ja: "か国" } },
        { num: "1", label: { zh: "認證夥伴", en: "Certified Partner", ja: "認証パートナー" } }
      ]
    };

    const ctaText = {
      zh: "了解我們的產品",
      en: "Explore Our Products",
      ja: "製品を見る"
    };

    const vis = this.state.visibleSections;

    return (
      <div id="body" className="coretech_page">
        <AnnouncementBanner lang={lang} />
        <Navbar lang={lang} updateLangFunc={this.update_lang} />
        <Breadcrumb lang={lang} items={[{ label: { zh: "核心技術", en: "Core Technology", ja: "コア技術" } }]} />

        {/* Hero */}
        <section className="ct_hero">
          <div className="ct_hero_grid" />
          <div className="ct_hero_content">
            <span className="ct_hero_tag">{hero.tag[lang]}</span>
            <h1 className="ct_hero_title">{hero.title[lang]}</h1>
            <p className="ct_hero_subtitle">{hero.subtitle[lang]}</p>
          </div>
          <div className="ct_hero_accent" />
        </section>

        {/* Intro */}
        <section
          className={"ct_intro" + (vis["intro"] ? " ct_visible" : "")}
          ref={el => this.registerRef(el, "intro")}
        >
          <div className="ct_intro_inner">
            <div className="ct_intro_line" />
            <p className="ct_intro_text">{introText[lang]}</p>
          </div>
        </section>

        {/* Three Pillars - Tab selector on top, content below */}
        <section className="ct_pillars">
          <div className="ct_pillars_tabs">
            {pillars.map((p, i) => (
              <button
                key={i}
                className={"ct_pillar_tab" + (this.state.activeTab === i ? " ct_pillar_tab_active" : "")}
                onClick={() => this.setState({ activeTab: i })}
                style={{ '--pillar-color': p.color }}
              >
                <span className="ct_pillar_tab_icon">{pillarIcons[p.iconIdx]}</span>
                <span>{p.label[lang]}</span>
              </button>
            ))}
          </div>

          {pillars.map((p, i) => (
            <div
              key={i}
              className={"ct_pillar_panel" + (this.state.activeTab === i ? " ct_pillar_panel_active" : "")}
            >
              <div className="ct_pillar_panel_inner">
                <div className="ct_pillar_accent_bar" style={{ background: p.color }} />
                <h3 className="ct_pillar_title">{p.title[lang]}</h3>

                {p.desc && <p className="ct_pillar_desc">{p.desc[lang]}</p>}

                {p.bullets && (
                  <ul className="ct_pillar_bullets">
                    {p.bullets[lang].map((b, bi) => (
                      <li key={bi}>
                        <span className="ct_bullet_marker" style={{ background: p.color }} />
                        {b}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ))}
        </section>

        {/* Patents & Research */}
        <section
          className={"ct_patents" + (vis["patents"] ? " ct_visible" : "")}
          ref={el => this.registerRef(el, "patents")}
        >
          <div className="ct_patents_inner">
            <h2 className="ct_patents_title">{patents.title[lang]}</h2>

            <div className="ct_patents_stats">
              {patents.stats.map((s, i) => (
                <div key={i} className="ct_stat_item">
                  <span className="ct_stat_num">{s.num}</span>
                  <span className="ct_stat_label">{s.label[lang]}</span>
                </div>
              ))}
            </div>

            <p className="ct_patents_desc">{patents.desc[lang]}</p>
          </div>
        </section>

        {/* CTA */}
        <section className="ct_cta">
          <button className="ct_cta_btn" onClick={() => this.navigate('/solutions')}>
            {ctaText[lang]} &rarr;
          </button>
        </section>

        <Footer lang={lang} navigate={(route) => this.navigate(route)} />
      </div>
    );
  }
}

export default CoreTechnologyPage;
